import { ActionType as FavoritesActionType, actionTypes as favoritesAT } from 'store/favorites';
import { actionTypes as programItemsAT } from 'store/programItems';
import { ActionType as TranspientPoolActionType, actionTypes as transientPoolAT } from 'store/transientPoolState';

import { ProgramItemDtoType } from 'types/services/magnolia/dtos/ProgramItemDto';
import { ProgramItemRecipeDtoType } from 'types/services/magnolia/dtos/ProgramItemRecipeDto';
import { ContentTypeRecipeDtoType } from '../../types/services/magnolia/dtos/ContentTypeRecipeDto';
import { RecipesActionType } from './recipes.actionCreators';
import { REQUEST_PERSIST_SELECTION, REQUEST_TOGGLE_TOOLTIP } from './recipes.actionTypes';

type RecipeItemType = ProgramItemRecipeDtoType & {
	externalId: string;
};

export type RecipeCalculatorBoundariesType = {
	increment: boolean;
	decrement: boolean;
};

export type RecipeTooltipVisibilityType = {
	increment: boolean;
	decrement: boolean;
};

export type RecipesExternalIdMapType = {
	[externalId: string]: number;
};

export type RecipesVariationListType = {
	[externalId: string]: number[];
};

export type RecipesTooltipVisibilityStateType = {
	[externalId: string]: RecipeTooltipVisibilityType;
};

export type RecipesStateType = {
	portionSelectorState: RecipesExternalIdMapType;
	portionVariationList: RecipesVariationListType;
	tooltipVisibilityState: RecipesTooltipVisibilityStateType;
};

export type RootStateType = {
	recipes: RecipesStateType;
};

export const initialState: RecipesStateType = {
	portionSelectorState: {},
	portionVariationList: {},
	tooltipVisibilityState: {},
};

type CombinedRecipesActionType = RecipesActionType | FavoritesActionType | TranspientPoolActionType;

export default (state: RecipesStateType = initialState, action: CombinedRecipesActionType) => {
	switch (action.type) {
		case programItemsAT.REPLACE_ITEM_CONTENTS:
			const { externalId, selectedContent, itemType } = action.programItemContents as any;

			if (itemType !== 'RECIPE') {
				return state;
			}

			return {
				...state,
				portionSelectorState: {
					...state.portionSelectorState,
					[externalId]: parseInt(selectedContent.baseSize.quantity, 10),
				},
				portionVariationList: {
					...state.portionVariationList,
					[externalId]: Object.keys(selectedContent.variants || {})
						.map((key: string): number => parseFloat(key.replace(',', '.')))
						.sort((a, b) => a - b),
				},
			};
		case programItemsAT.REQUEST_CONTENTS_SUCCESS:
			const mightyRecipes: RecipeItemType[] = action.programItemsContents.filter(
				(programItem: ProgramItemDtoType) => {
					return programItem.itemType === 'RECIPE';
				}
			) as any;

			const portionSelectorState = mightyRecipes.reduce(
				(prev: RecipesExternalIdMapType, curr: RecipeItemType): RecipesExternalIdMapType => ({
					...prev,
					[curr.externalId]: parseInt(curr.selectedContent.baseSize.quantity, 10),
				}),
				{}
			);

			const portionVariationList = mightyRecipes.reduce(
				(prev: RecipesVariationListType, curr: RecipeItemType): RecipesVariationListType => ({
					...prev,
					[curr.externalId]: Object.keys(curr.selectedContent.variants || {})
						.map((key: string): number => parseFloat(key.replace(',', '.')))
						.sort((a, b) => a - b),
				}),
				{}
			);

			return {
				...state,
				portionSelectorState,
				portionVariationList,
			};

		// adopt REQUEST_CONTENTS_SUCCESS to the single loading process
		case favoritesAT.REQUEST_SELECTED_FAVORITE_DATA_SUCCESS:
			if (!action.item || action.item.contentType !== 'RECIPE') {
				return state;
			}

			const recipeItem: ContentTypeRecipeDtoType = action.item;

			const singlePortionSelectorState = {
				[recipeItem.externalId]: parseInt(recipeItem.baseSize.quantity, 10),
			};

			const singlePortionVariationList = {
				[recipeItem.externalId]: Object.keys(recipeItem.variants || {})
					.map((key: string): number => parseFloat(key.replace(',', '.')))
					.sort((a, b) => a - b),
			};

			return {
				...state,
				portionSelectorState: singlePortionSelectorState,
				portionVariationList: singlePortionVariationList,
			};

		// adopt REQUEST_CONTENTS_SUCCESS... to single pool item success process
		case transientPoolAT.REQUEST_SELECTED_ITEM_SUCCESS:
			if (!action.content || action.content.contentType !== 'RECIPE') {
				return state;
			}

			const selectedRecipeItem: ContentTypeRecipeDtoType = action.content as any;

			const poolPortionSelectorState = {
				[selectedRecipeItem.externalId]: parseInt(selectedRecipeItem.baseSize.quantity, 10),
			};

			// TODO: Outsource generation of list into own function to fix duplication of code
			const poolPortionVariationList = {
				[selectedRecipeItem.externalId]: Object.keys(selectedRecipeItem.variants || {})
					.map((key: string): number => parseFloat(key.replace(',', '.')))
					.sort((a, b) => a - b),
			};

			return {
				...state,
				portionSelectorState: poolPortionSelectorState,
				portionVariationList: poolPortionVariationList,
			};

		case REQUEST_TOGGLE_TOOLTIP:
			return {
				...state,
				tooltipVisibilityState: {
					...state.tooltipVisibilityState,
					[action.externalId]: {
						...state.tooltipVisibilityState[action.externalId],
						[action.dir === 'inc' ? 'increment' : 'decrement']: action.visible,
					},
				},
			};

		case REQUEST_PERSIST_SELECTION:
			return {
				...state,
				portionSelectorState: {
					...state.portionSelectorState,
					[action.externalId]: action.portionValue,
				},
			};
		default:
			return state;
	}
};
